import { tv } from 'tailwind-variants'

import { typography } from '@gto/ui/typography'

/**
 * Button style variants using tailwind-variants
 *
 * This defines all the visual styles for the Button component.
 * Uses compound variants to apply color-specific Tailwind classes directly,
 * leveraging design tokens defined in the Tailwind preset (colors.button.*).
 */
export const buttonVariants = tv({
  slots: {
    root: [
      // Base styles
      'relative inline-flex items-center justify-center gap-2 [&_svg]:inline-block',
      'rounded-sm border-none cursor-pointer',
      'transition-[box-shadow,background-color,border-color,color] duration-150',
      'select-none whitespace-nowrap',
      // Focus ring using ::after pseudo-element
      'focus-visible:outline-none',
      'after:pointer-events-none after:absolute after:inset-[2px] after:rounded-[4px] after:border after:border-solid after:border-transparent',
    ],
    iconWrapper: ['inline-flex shrink-0 items-center', '[&>svg]:size-[1em]'],
    spinnerWrapper: ['inline-flex shrink-0 items-center', '[&>svg]:size-[24px]'],
  },
  variants: {
    // Button variants - structural styles only (colors applied via compound variants)
    variant: {
      contained: {},
      outlined: {
        root: ['bg-transparent', 'border border-solid'],
      },
      text: {
        root: ['bg-transparent'],
      },
    },
    // Color variants - used for compound variant matching
    color: {
      primary: {},
      secondary: {},
      error: {},
      warning: {},
      info: {},
    },
    accent: {
      custom: {},
    },
    // Size variants
    size: {
      small: {
        root: [typography['button/small'], 'px-2 py-1 h-6'],
        iconWrapper: '[&>svg]:!size-[16px]',
        spinnerWrapper: '[&>svg]:!size-[16px]',
      },
      medium: {
        root: [typography['button/medium'], 'px-3 py-1 h-8'],
        iconWrapper: '[&>svg]:!size-[20px]',
        spinnerWrapper: '[&>svg]:!size-[20px]',
      },
      large: {
        root: [typography['button/large'], 'px-3 py-2 h-12 min-w-[75px]'],
        iconWrapper: '[&>svg]:!size-[32px]',
        spinnerWrapper: '[&>svg]:!size-[32px]',
      },
    },
    // Full width
    fullWidth: {
      true: {
        root: 'w-full',
      },
    },
    // Disabled state
    disabled: {
      true: {
        root: 'cursor-default',
      },
      false: {},
    },
    // Loading state
    loading: {
      true: {
        root: 'cursor-wait',
      },
    },
    /**
     * Surface the button sits on. `onPrimary` is a primary-green fill (the Fair
     * Play top bar): the palette's own fills either vanish into that green (a
     * primary CTA) or are tuned for a dark ground (the secondary grey), so the
     * colour pairs invert here rather than at the call site.
     */
    tone: {
      default: {},
      onPrimary: {},
    },
  },
  compoundVariants: [
    // ========================================
    // CONTAINED VARIANT - Color combinations
    // ========================================
    {
      variant: 'contained',
      color: 'primary',
      className: {
        root: ['bg-button-primary-main text-button-primary-contrastText'],
      },
    },
    {
      variant: 'contained',
      color: 'secondary',
      className: {
        root: ['bg-button-secondary-main text-button-secondary-contrastText'],
      },
    },
    {
      variant: 'contained',
      color: 'error',
      className: {
        root: ['bg-button-error-main text-button-error-contrastText'],
      },
    },
    {
      variant: 'contained',
      color: 'warning',
      className: {
        root: ['bg-button-warning-main text-button-warning-contrastText'],
      },
    },
    {
      variant: 'contained',
      color: 'info',
      className: {
        root: ['bg-button-info-main text-button-info-contrastText'],
      },
    },

    // ========================================
    // OUTLINED VARIANT - Color combinations
    // ========================================
    {
      variant: 'outlined',
      color: 'primary',
      className: {
        root: ['text-button-primary-main border-button-primary-outline'],
      },
    },
    {
      variant: 'outlined',
      color: 'secondary',
      className: {
        root: ['text-text-secondary border-button-secondary-outline'],
      },
    },
    {
      variant: 'outlined',
      color: 'error',
      className: {
        root: ['text-button-error-main border-button-error-outline'],
      },
    },
    {
      variant: 'outlined',
      color: 'warning',
      className: {
        root: ['text-button-warning-main border-button-warning-outline'],
      },
    },
    {
      variant: 'outlined',
      color: 'info',
      className: {
        root: ['text-button-info-main border-button-info-outline'],
      },
    },

    // ========================================
    // TEXT VARIANT - Color combinations
    // ========================================
    {
      variant: 'text',
      color: 'primary',
      className: {
        root: ['text-button-primary-main'],
      },
    },
    {
      variant: 'text',
      color: 'secondary',
      className: {
        root: ['text-text-secondary'],
      },
    },
    {
      variant: 'text',
      color: 'error',
      className: {
        root: ['text-button-error-main'],
      },
    },
    {
      variant: 'text',
      color: 'warning',
      className: {
        root: ['text-button-warning-main'],
      },
    },
    {
      variant: 'text',
      color: 'info',
      className: {
        root: ['text-button-info-main'],
      },
    },

    // ========================================
    // INTERACTIVE STATES
    // ========================================
    {
      variant: 'contained',
      disabled: false,
      className: {
        root: ['active:hover:shadow-[none]'],
      },
    },
    {
      variant: 'outlined',
      disabled: false,
      className: {
        root: [
          '[&:focus-visible:not(:active)]:border-transparent',
          'active:focus-visible:after:hidden',
        ],
      },
    },
    {
      variant: 'text',
      disabled: false,
      className: {
        root: ['active:focus-visible:after:hidden'],
      },
    },
    {
      variant: 'contained',
      color: 'primary',
      disabled: false,
      className: {
        root: [
          'hover:shadow-glow-primary',
          '[&:focus-visible:not(:active)]:after:border-button-primary-contrastText',
          'active:hover:bg-primary-dark',
        ],
      },
    },
    {
      variant: 'contained',
      color: 'secondary',
      disabled: false,
      className: {
        root: [
          // No glow for secondary
          'hover:bg-secondary-dark',
          '[&:focus-visible:not(:active)]:after:border-button-secondary-contrastText',
          'active:hover:bg-secondary-main',
        ],
      },
    },
    {
      variant: 'contained',
      color: 'error',
      disabled: false,
      className: {
        root: [
          'hover:shadow-glow-error',
          '[&:focus-visible:not(:active)]:after:border-button-error-contrastText',
          'active:hover:bg-error-dark',
        ],
      },
    },
    {
      variant: 'contained',
      color: 'warning',
      disabled: false,
      className: {
        root: [
          'hover:shadow-glow-warning',
          '[&:focus-visible:not(:active)]:after:border-button-warning-contrastText',
          'active:hover:bg-warning-dark',
        ],
      },
    },
    {
      variant: 'contained',
      color: 'info',
      disabled: false,
      className: {
        root: [
          'hover:shadow-glow-info',
          '[&:focus-visible:not(:active)]:after:border-button-info-contrastText',
          'active:hover:bg-info-dark',
        ],
      },
    },
    {
      variant: 'outlined',
      color: 'primary',
      disabled: false,
      className: {
        root: [
          '[&:focus-visible:not(:active)]:bg-button-primary-focus',
          '[&:focus-visible:not(:active)]:after:border-button-primary-main',
          'active:border-button-primary-outline active:bg-button-primary-selected',
        ],
      },
    },
    {
      variant: 'outlined',
      color: 'secondary',
      disabled: false,
      className: {
        root: [
          'hover:text-text-primary focus-visible:text-text-primary active:text-text-primary',
          '[&:focus-visible:not(:active)]:bg-button-secondary-focus',
          '[&:focus-visible:not(:active)]:after:border-white',
          'active:border-button-secondary-outline active:bg-button-secondary-selected',
        ],
      },
    },
    {
      variant: 'outlined',
      color: 'error',
      disabled: false,
      className: {
        root: [
          '[&:focus-visible:not(:active)]:bg-button-error-focus',
          '[&:focus-visible:not(:active)]:after:border-button-error-main',
          'active:border-button-error-outline active:bg-button-error-selected',
        ],
      },
    },
    {
      variant: 'outlined',
      color: 'warning',
      disabled: false,
      className: {
        root: [
          '[&:focus-visible:not(:active)]:bg-button-warning-focus',
          '[&:focus-visible:not(:active)]:after:border-button-warning-main',
          'active:border-button-warning-outline active:bg-button-warning-selected',
        ],
      },
    },
    {
      variant: 'outlined',
      color: 'info',
      disabled: false,
      className: {
        root: [
          '[&:focus-visible:not(:active)]:bg-button-info-focus',
          '[&:focus-visible:not(:active)]:after:border-button-info-main',
          'active:border-button-info-outline active:bg-button-info-selected',
        ],
      },
    },
    {
      variant: 'text',
      color: 'primary',
      disabled: false,
      className: {
        root: [
          'hover:bg-button-primary-hover',
          '[&:focus-visible:not(:active)]:bg-button-primary-focus',
          '[&:focus-visible:not(:active)]:after:border-button-primary-main',
          'active:bg-button-primary-selected',
        ],
      },
    },
    {
      variant: 'text',
      color: 'secondary',
      disabled: false,
      className: {
        root: [
          '[&:hover:not(:active)]:text-text-primary [&:focus-visible:not(:active)]:text-text-primary',
          'hover:bg-button-secondary-hover',
          '[&:focus-visible:not(:active)]:bg-button-secondary-focus',
          '[&:focus-visible:not(:active)]:after:border-white',
          'active:bg-button-secondary-selected',
        ],
      },
    },
    {
      variant: 'text',
      color: 'error',
      disabled: false,
      className: {
        root: [
          'hover:bg-button-error-hover',
          '[&:focus-visible:not(:active)]:bg-button-error-focus',
          '[&:focus-visible:not(:active)]:after:border-button-error-main',
          'active:bg-button-error-selected',
        ],
      },
    },
    {
      variant: 'text',
      color: 'warning',
      disabled: false,
      className: {
        root: [
          'hover:bg-button-warning-hover',
          '[&:focus-visible:not(:active)]:bg-button-warning-focus',
          '[&:focus-visible:not(:active)]:after:border-button-warning-main',
          'active:bg-button-warning-selected',
        ],
      },
    },
    {
      variant: 'text',
      color: 'info',
      disabled: false,
      className: {
        root: [
          'hover:bg-button-info-hover',
          '[&:focus-visible:not(:active)]:bg-button-info-focus',
          '[&:focus-visible:not(:active)]:after:border-button-info-main',
          'active:bg-button-info-selected',
        ],
      },
    },

    // ========================================
    // DISABLED STATES
    // ========================================
    {
      disabled: true,
      variant: 'contained',
      className: {
        root: 'bg-secondary-disabled text-text-action-disabled',
      },
    },
    {
      disabled: true,
      variant: 'outlined',
      className: {
        root: 'border-text-action-disabled-background text-text-action-disabled',
      },
    },
    {
      disabled: true,
      variant: 'text',
      className: {
        root: 'text-text-action-disabled',
      },
    },
    // ========================================
    // ON-PRIMARY TONE - inverted pairs for a primary-green surface
    // ========================================
    // The emphasised action: green-on-green is invisible, so it inverts to the
    // bar's dark ink carrying a green label — still the loudest control there.
    {
      tone: 'onPrimary',
      variant: 'contained',
      color: 'primary',
      className: {
        root: ['bg-black text-primary-main hover:bg-black/[0.85]'],
      },
    },
    // Its quieter sibling (e.g. "Go Elite") keeps the outline, in the bar's ink.
    {
      tone: 'onPrimary',
      variant: 'outlined',
      color: 'primary',
      className: {
        root: ['border-primary-contrastText text-primary-contrastText hover:bg-black/[0.1]'],
      },
    },
    // The neutral filled control (Upload) matches the on-primary switcher's
    // selected pill, so the bar's dark controls read as one family.
    {
      tone: 'onPrimary',
      variant: 'contained',
      color: 'secondary',
      className: {
        root: ['bg-black/[0.8] text-white hover:bg-black'],
      },
    },
    {
      tone: 'onPrimary',
      variant: 'text',
      color: 'secondary',
      className: {
        root: ['text-primary-contrastText hover:bg-black/[0.1]'],
      },
    },
    {
      accent: 'custom',
      variant: 'contained',
      disabled: false,
      className: {
        root: 'bg-[color:var(--accent-solid,#aafbb2)] active:hover:bg-[color:var(--accent-solid,#aafbb2)]',
      },
    },
    {
      accent: 'custom',
      variant: 'outlined',
      color: 'primary',
      disabled: false,
      className: {
        root: [
          'border-transparent bg-[rgba(var(--accent-rgb,170,251,178),var(--accent-fill-a,0.07))] text-[color:var(--accent-solid,#aafbb2)]',
          'shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]',
          'hover:bg-[rgba(var(--accent-rgb,170,251,178),var(--accent-hover-fill-a,0.1))] hover:text-[color:var(--accent-solid,#aafbb2)] hover:shadow-[inset_0_0_0_1px_rgba(var(--accent-rgb,170,251,178),0.45)]',
          'active:bg-[rgba(var(--accent-rgb,170,251,178),var(--accent-fill-a,0.07))] active:text-[color:var(--accent-solid,#aafbb2)]',
        ],
      },
    },
    {
      accent: 'custom',
      variant: 'outlined',
      color: 'secondary',
      disabled: false,
      className: {
        root: [
          'border-transparent bg-secondary-main text-white/[0.85] shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]',
          'hover:bg-[rgba(var(--accent-rgb,170,251,178),var(--accent-hover-fill-a,0.1))] hover:text-[color:var(--accent-solid,#aafbb2)] hover:shadow-[inset_0_0_0_1px_rgba(var(--accent-rgb,170,251,178),0.45)]',
          'active:bg-secondary-main active:text-white/[0.85]',
        ],
      },
    },
    {
      accent: 'custom',
      variant: 'outlined',
      disabled: true,
      className: {
        root: 'border-transparent bg-secondary-disabled text-white/[0.22]',
      },
    },
  ],
  defaultVariants: {
    tone: 'default',
    variant: 'text',
    color: 'primary',
    size: 'medium',
    fullWidth: false,
    disabled: false,
    loading: false,
  },
})

export type ButtonVariants = Parameters<typeof buttonVariants>[0]
